import { LayoutModule } from '@angular/cdk/layout';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { APP_BASE_HREF } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { HammerModule } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { SearchModule } from '@ecs-features/search/search.module';
import { breakpoints, mediaQueries, tracking } from '@ecs-helpers';
import { LoadingLayerService } from '@ecs-services/loading-layer.service';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import {
    ADD_REQUESTED_LANGUAGE_HEADER,
    LanguageModule,
    MediaQueryModule,
    MockBackendInterceptor,
    OsApiAuthInterceptor,
    OsApiAuthModule,
    TrackingModule,
    TranslateInterceptor,
    TRANSLATION_FILES,
    TranslationService,
    WINDOW,
} from '@os-components';
import { routes } from '../app/app-routing.module';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection(),
        provideRouter(routes),
        provideHttpClient(withInterceptorsFromDi()),
        importProvidersFrom(
            StoreModule.forRoot({}),
            EffectsModule.forRoot(),
            TrackingModule.forRoot({ appName: tracking.app, events: tracking.step }),
            OsApiAuthModule.forRoot(
                {
                    xCsrfTokenUrl: `${environment.baseURLs.api.domain}${environment.baseURLs.api.token}`,
                },
                [
                    {
                        path: '/online-support',
                        apiUrl: `${environment.baseURLs.api.domain}${environment.baseURLs.api.os}`,
                        useXCsrfToken: true,
                        withCredentials: true,
                    },
                    {
                        accessTokenApiName: environment.auth.clientId ? 'WZ' : '',
                        path: '/api',
                        apiUrl: `${environment.baseURLs.api.domain}${environment.baseURLs.api.path}`,
                        useXCsrfToken: true,
                        withCredentials: true,
                        retriggerAfterLoginChange: true,
                    },
                    {
                        path: '/translate',
                        apiUrl: `${environment.baseURLs.api.domain}${environment.baseURLs.api.translation}`,
                        useXCsrfToken: false,
                        withCredentials: false,
                    },
                    {
                        path: '/workbench-api',
                        apiUrl: `${environment.baseURLs.api.workbench}`,
                        useXCsrfToken: false,
                        withCredentials: false,
                        headers: {
                            'sew-accessory-token': environment.tokens.accessory,
                        },
                    },
                    {
                        path: '/caas',
                        apiUrl: environment.caas.baseUrl,
                        useXCsrfToken: false,
                        withCredentials: false,
                    },
                ]
            ),
            MediaQueryModule.forRoot({ breakpoints, mediaQueries }),
            LanguageModule.forRoot(environment.defaultLang),
            HammerModule,
            PortalModule,
            LayoutModule,
            OverlayModule,
            SearchModule,
            TranslateModule.forRoot({
                defaultLanguage: environment.defaultLang,
                loader: {
                    provide: TranslateLoader,
                    useClass: TranslationService,
                },
            }),
            ...(environment.production ? [] : [StoreDevtoolsModule.instrument()])
        ),
        { provide: APP_BASE_HREF, useValue: environment.baseURLs.href },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: OsApiAuthInterceptor,
            multi: true,
        },
        ...(environment.production === false && environment.useMockServer
            ? [
                  {
                      provide: HTTP_INTERCEPTORS,
                      useClass: MockBackendInterceptor,
                      multi: true,
                  },
              ]
            : []),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TranslateInterceptor,
            multi: true,
        },
        {
            provide: ADD_REQUESTED_LANGUAGE_HEADER,
            useValue: true,
        },
        { provide: TRANSLATION_FILES, useValue: ['ECS', 'LIB'] },
        {
            provide: WINDOW,
            useValue: window,
        },
        TranslationService,
        LoadingLayerService,
    ],
};
